import { useEffect, useMemo, useState } from "react";
import {
  Wallet, Plus, CheckCircle2, Clock, XCircle, Banknote, X,
  CreditCard, Send, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

function fmt(n) { return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function shortDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso.slice(0, 16).replace("T", " "); }
}

function StatusBadge({ status }) {
  const map = {
    paid: { cls: "p-pill-emerald", icon: CheckCircle2, label: "Paid" },
    approved: { cls: "p-pill-blue", icon: CheckCircle2, label: "Approved" },
    pending: { cls: "p-pill-amber", icon: Clock, label: "Pending" },
    rejected: { cls: "p-pill-rose", icon: XCircle, label: "Rejected" },
  };
  const m = map[status] || { cls: "p-pill-slate", icon: Clock, label: status || "—" };
  const I = m.icon;
  return <span className={`p-pill ${m.cls}`}><I size={10} /> {m.label}</span>;
}

const METHOD_META = {
  "Binance Pay":   { sub: "Crypto exchange wallet", logo: "/payment-logos/binance.png",    bg: "#0b0b10" },
  "USDT BEP-20":   { sub: "BSC · BEP-20",            logo: "/payment-logos/usdt-bep20.png", bg: "#0b0b10" },
  "USDT TRC-20":   { sub: "Tron · TRC-20",           logo: "/payment-logos/usdt.svg",       bg: "#ffffff" },
  "Bkash":         { sub: "Local mobile banking",    logo: "/payment-logos/bkash-real.png", bg: "#e2136e" },
  "Nagad":         { sub: "Local mobile banking",    logo: "/payment-logos/nagad-real.png", bg: "#ffffff" },
};

export default function Payouts() {
  const [rows, setRows] = useState([]);
  const [methods, setMethods] = useState([]);
  const [available, setAvailable] = useState(0);
  const [lifetimePaid, setLifetimePaid] = useState(0);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get("/aff-panel/payouts"),
        api.get("/aff-panel/dashboard"),
      ]);
      setRows(pRes.data.rows || []);
      setMethods(pRes.data.methods || []);
      setAvailable(dRes.data?.totals?.available_for_payout ?? 0);
      setLifetimePaid(dRes.data?.totals?.lifetime_paid ?? 0);
      setPending(dRes.data?.totals?.in_flight_payouts ?? 0);
    } catch (e) { toast.error("Failed to load payouts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalRequested = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  return (
    <div>
      <PageHeader
        title="Payouts"
        eyebrow="Cash out"
        subtitle="Choose between crypto rails (Binance, USDT) or local Bkash / Nagad. Processed within 24 hours."
        right={
          <button onClick={() => setOpen(true)} className="px-4 h-11 rounded-xl inline-flex items-center gap-2 p-btn-gold text-[12.5px]" data-testid="new-payout-btn">
            <Plus size={15} /> New Payout Request
          </button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
        <StatCard label="Available" value={available} prefix="৳ " decimals={2} accent="emerald" icon={Banknote} hint="Ready to withdraw" delay={0} />
        <StatCard label="In-Flight" value={pending} prefix="৳ " decimals={2} accent="amber" icon={Clock} hint="Pending / approved" delay={60} />
        <StatCard label="Total Requests" value={rows.length} accent="slate" icon={Send} hint={`৳ ${fmt(totalRequested)} requested‑lifetime`} delay={120} />
        <StatCard label="Lifetime Paid" value={lifetimePaid} prefix="৳ " decimals={2} accent="gold" icon={CreditCard} delay={180} />
      </div>

      {/* Payment methods strip */}
      <div className="p-glass p-fade-up mb-5" style={{ padding: "18px 20px" }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-emerald-300" />
          <div className="text-sm font-bold text-white">Supported Payout Methods</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {(methods.length ? methods : Object.keys(METHOD_META)).map((m) => {
            const meta = METHOD_META[m] || { sub: "", logo: "", bg: "#0b0b10" };
            return (
              <div key={m} className="p-glass-light p-3 text-center" style={{ border: "1px solid rgba(34, 192, 115, 0.18)" }} data-testid={`payout-method-card-${m}`}>
                <div
                  className="mx-auto mb-1.5 rounded-lg overflow-hidden flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: meta.bg || "#0b0b10",
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                  }}
                >
                  {meta.logo ? (
                    <img
                      src={meta.logo}
                      alt={m}
                      loading="lazy"
                      style={{ width: "82%", height: "82%", objectFit: "contain" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <span className="text-white/70 text-sm font-bold">{(m || "?").slice(0, 1)}</span>
                  )}
                </div>
                <div className="text-[12px] font-bold text-white">{m}</div>
                <div className="text-[10px] text-white/45 mt-0.5">{meta.sub}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-glass p-fade-up p-fade-up-1" style={{ padding: "16px 18px" }}>
        {loading ? (
          <div className="py-10 text-center">
            <div className="p-skeleton mx-auto" style={{ width: 200, height: 14 }} />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <div className="inline-flex w-16 h-16 rounded-full items-center justify-center mb-3" style={{
              background: "linear-gradient(135deg, rgba(34, 192, 115, 0.18), rgba(34, 192, 115, 0.02))",
              border: "1px solid rgba(34, 192, 115, 0.4)",
            }}>
              <Wallet size={26} className="text-emerald-300" />
            </div>
            <div className="text-white/65 text-sm font-semibold">No payout requests yet</div>
            <button onClick={() => setOpen(true)} className="mt-3 text-emerald-300 text-[12.5px] underline font-bold">Submit your first payout</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="p-table" data-testid="payouts-table">
              <thead><tr>
                <th>Requested</th>
                <th>Method</th>
                <th>Destination</th>
                <th className="text-right">Amount</th>
                <th>Status</th>
                <th>Tx Ref / Notes</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="text-white/75">{shortDateTime(r.created_at)}</td>
                    <td>
                      <div className="font-bold">{r.method}</div>
                      <div className="text-[10.5px] text-white/45">{METHOD_META[r.method]?.sub}</div>
                    </td>
                    <td className="font-mono text-[11.5px] break-all max-w-[220px] text-emerald-100/85">{r.destination}</td>
                    <td className="text-right"><span className="p-gold-text font-black">৳ {fmt(r.amount)}</span></td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-white/70 text-[11.5px]">
                      {r.tx_ref ? <span className="font-mono">{r.tx_ref}</span> : "—"}
                      {r.admin_notes && <div className="text-white/45 mt-0.5">{r.admin_notes}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && <NewPayoutModal methods={methods} available={available} onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}

function NewPayoutModal({ methods, available, onClose, onSaved }) {
  const [method, setMethod] = useState(methods[0] || "");
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState("");
  const [holder, setHolder] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!method) return toast.error("Choose a method");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!destination.trim()) return toast.error("Destination address/number required");
    if (amt > available + 0.01) return toast.error(`Amount exceeds available balance ৳${fmt(available)}`);
    setBusy(true);
    try {
      await api.post("/aff-panel/payouts", {
        amount: amt, method, destination: destination.trim(),
        holder_name: holder.trim(), notes: notes.trim(),
      });
      toast.success("Payout requested");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  const destLabel = (() => {
    if (method === "Binance Pay") return "Binance Pay ID / Email";
    if (method === "USDT BEP-20") return "USDT BEP-20 Wallet Address";
    if (method === "USDT TRC-20") return "USDT TRC-20 Wallet Address";
    if (method === "Bkash") return "Bkash Number";
    if (method === "Nagad") return "Nagad Number";
    return "Destination";
  })();

  return (
    <div className="p-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="p-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="p-gold-chip mb-2"><Banknote size={11} /> Withdrawal Request</div>
            <h3 className="text-[22px] font-black p-platinum-text">New Payout Request</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg p-btn-ghost"><X size={16} /></button>
        </div>
        <div className="rounded-xl p-3.5 mb-4 flex items-center justify-between" style={{
          background: "linear-gradient(135deg, rgba(16,185,129,0.14), transparent)",
          border: "1px solid rgba(16,185,129,0.35)",
        }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Available balance</div>
            <div className="text-[22px] font-black p-gold-text">৳ {fmt(available)}</div>
          </div>
          <Wallet size={26} className="text-emerald-300/70" />
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="new-payout-form">
          <div>
            <label className="p-label">Method</label>
            <div className="p-input-shell">
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payout-method">
                {methods.map((m) => <option key={m} value={m} className="bg-slate-900">{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="p-label">Amount (৳)</label>
            <div className="p-input-shell">
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="payout-amount" />
              <button type="button" onClick={() => setAmount(String(available))} className="text-[10px] font-bold tracking-widest text-emerald-300 hover:text-emerald-200 uppercase">Max</button>
            </div>
          </div>
          <div>
            <label className="p-label">{destLabel}</label>
            <div className="p-input-shell">
              <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="" style={{ fontFamily: "ui-monospace, Menlo, monospace" }} data-testid="payout-destination" />
            </div>
          </div>
          <div>
            <label className="p-label">Account Holder Name (optional)</label>
            <div className="p-input-shell">
              <input value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Full name" />
            </div>
          </div>
          <div>
            <label className="p-label">Notes (optional)</label>
            <div className="p-input-shell" style={{ height: "auto", padding: 12 }}>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any extra info" style={{ resize: "vertical" }} />
            </div>
          </div>
          <button type="submit" disabled={busy} className="w-full h-12 rounded-xl p-btn-gold text-[13px] inline-flex items-center justify-center gap-2" data-testid="payout-submit">
            <Send size={15} /> {busy ? "Submitting…" : "Submit Request"}
          </button>
        </form>
      </div>
    </div>
  );
}
